import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { companyInfo, servicePages } from "@/mock";

function ServiceDetail({ divisionSlug }) {
  const { serviceSlug } = useParams();
  const page = servicePages?.[divisionSlug]?.[serviceSlug];

  if (!page) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagina non trovata</h1>
          <Link to={`/${divisionSlug}`}>
            <Button className="bg-orange-600 hover:bg-orange-700 text-white">Torna alla divisione</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 transition-all duration-300">
        <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_renewable-energy-hub-19/artifacts/wxu8fra2_logo-MTSenergia-arancio-1.png.webp"
              alt="MTS Energia Logo"
              className="h-14 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#about" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Chi Siamo
            </Link>
            <Link to="/#divisions" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Servizi
            </Link>
            <Link to="/#projects" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Portfolio
            </Link>
            <Link to="/codice-etico" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Codice Etico
            </Link>
            <Link to="/#contact" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Contatti
            </Link>
            <Link to="/#contact">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 transition-all duration-300 hover:scale-105">
                Richiedi Preventivo
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      <section className="pt-32 pb-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <Badge className="mb-4 bg-blue-100 text-blue-700 px-4 py-2">{page.badge}</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">{page.title}</h1>
            {page.subtitle ? <p className="text-lg md:text-xl text-gray-600 mt-4">{page.subtitle}</p> : null}
          </div>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="w-full">
              <Carousel className="w-full">
                <CarouselContent>
                  {page.images.map((src, idx) => (
                    <CarouselItem key={idx}>
                      <div className="p-1">
                        <div className="overflow-hidden rounded-2xl border border-gray-100 shadow-lg">
                          <img src={src} alt={`${page.title} ${idx + 1}`} className="w-full h-[320px] md:h-[420px] object-cover" />
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </div>

            <div className="space-y-6">
              <Card className="border border-gray-100 shadow-lg rounded-2xl">
                <CardContent className="p-8">
                  {page.paragraphs?.length ? (
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      {page.paragraphs.map((p, idx) => (
                        <p key={idx}>{p}</p>
                      ))}
                    </div>
                  ) : null}

                  {page.bullets?.length ? (
                    <ul className="mt-6 space-y-3">
                      {page.bullets.map((b, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-700">
                          <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="mt-8 flex flex-col md:flex-row gap-3 md:justify-center">
                    <Link to={`/${divisionSlug}`} className="w-full md:w-auto">
                      <Button
                        variant="outline"
                        className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-4 text-base font-semibold w-full md:w-[240px]"
                      >
                        Torna alla Divisione <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <Link to="/#contact" className="w-full md:w-auto">
                      <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-4 text-base font-semibold w-full md:w-[240px]">
                        Inizia il Tuo Progetto <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              <div className="text-sm text-gray-500">
                {companyInfo.legalName} • {companyInfo.address}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ServiceDetail;
